/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Tab bar subrayado/segmento reutilizable (Caja, Equipo, …).
 * No depende de módulos de dominio.
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, type ViewStyle } from 'react-native';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { ticketColors } from '../theme/ticketColors';

export type UnderlineTabOption<T extends string> = {
  value: T;
  label: string;
  /** Punto ámbar — aviso sin texto largo en la etiqueta. */
  attention?: boolean;
};

type Props<T extends string> = {
  options: UnderlineTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  style?: ViewStyle;
  /** Prefijo de testID por tab → `${prefix}.${value}`. Default: `underline-tab`. */
  testIDPrefix?: string;
  /** Estira cada tab a ancho igual (mobile chrome). Default: compacto a la izquierda. */
  fillTabs?: boolean;
};

/** Tabs subrayados (proto sectabs): hairline inferior + activo brand-ink con subrayado brand. */
export function UnderlineTabBar<T extends string>({
  options,
  value,
  onChange,
  style,
  testIDPrefix = 'underline-tab',
  fillTabs = false,
}: Props<T>) {
  return (
    <View style={[styles.tabBar, style]} accessibilityRole="tablist">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <TouchableOpacity
            key={option.value}
            style={[styles.tabItem, fillTabs && styles.tabItemFill, selected && styles.tabItemSelected]}
            onPress={() => onChange(option.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            accessibilityLabel={
              option.attention ? `${option.label}, requiere revisión` : option.label
            }
            testID={`${testIDPrefix}.${option.value}`}
          >
            <View style={styles.labelRow}>
              <Text style={[styles.tabText, selected && styles.tabTextSelected]}>
                {option.label}
              </Text>
              {option.attention ? <View style={styles.attentionDot} /> : null}
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 2,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  tabItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginBottom: -1,
  },
  tabItemFill: {
    flexGrow: 1,
  },
  tabItemSelected: {
    borderBottomColor: ticketColors.brandPrimary,
  },
  tabText: {
    ...typography.body,
    fontSize: 14,
    lineHeight: 20,
    color: ticketColors.ink2,
    fontWeight: '700',
    textAlign: 'center',
  },
  tabTextSelected: {
    color: ticketColors.brandInk,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  attentionDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: ticketColors.warn,
  },
});
